import React, { useMemo } from 'react';

import { Alert, Box, Button, CircularProgress, LinearProgress, Paper, Stack, Typography } from '@mui/material';

import PromoLink from '~/components/Promo/PromoLink';

import { useGetPromoActivitySummary } from '~/hooks/apis/promo/useGetPromoActivitySummary';

import { type PromoActivitySummaryTotals } from '~/services/PromoService';
import { formatDateWithClock } from '~/services/TimeService';

const PROMO_CARD_LIMIT = 5;
const PARENT_PROMO_CARD_LIMIT = 3;

type ActivityEntity = PromoActivitySummaryTotals & {
  id: string;
};

type ActivityMetric = {
  label: string;
  value: number;
  tone?: 'error';
};

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to load recent promo activity.';
}

function getMostActiveEntities(
  entities: Record<string, PromoActivitySummaryTotals>,
  limit: number,
): Array<ActivityEntity> {
  return Object.entries(entities)
    .map(([id, totals]) => ({ id, ...totals }))
    .sort((first, second) => second.eventCount - first.eventCount || first.id.localeCompare(second.id))
    .slice(0, limit);
}

function getApplyRate(totals: PromoActivitySummaryTotals): number {
  const { requestCount, successfulCount } = totals.recentApplyStats;
  return requestCount === 0 ? 0 : Math.round((successfulCount / requestCount) * 100);
}

function renderActivityMetricCard(metric: ActivityMetric) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: '1 1 140px',
        minWidth: 0,
        p: 2,
        ...(metric.tone === 'error' ? { borderColor: 'error.main' } : {}),
      }}
    >
      <Typography color={metric.tone === 'error' ? 'error.main' : 'text.secondary'} variant="body2">
        {metric.label}
      </Typography>
      <Typography
        color={metric.tone === 'error' ? 'error.main' : 'text.primary'}
        component="p"
        sx={{ fontSize: '2rem', fontWeight: 'fontWeightBold', lineHeight: 1.2, mt: 0.5 }}
      >
        {metric.value.toLocaleString()}
      </Typography>
    </Paper>
  );
}

function renderPromoActivityCard(entity: ActivityEntity, isParentPromo = false) {
  const applyRate = getApplyRate(entity);
  const failures = entity.recentApplyStats.unsuccessfulRequestCount + entity.recentApplyStats.unsuccessfulLogCount;

  return (
    <Paper component="article" variant="outlined" sx={{ flex: '1 1 260px', minWidth: 0, p: 2 }}>
      <Stack spacing={1.5}>
        <PromoLink promoId={entity.id} title={isParentPromo ? 'Parent promo' : 'Promo'} copyValue={entity.id} />
        <Box>
          <Typography component="p" sx={{ fontSize: '2rem', fontWeight: 'fontWeightBold', lineHeight: 1.2 }}>
            {entity.recentApplyStats.successfulCount.toLocaleString()}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Applies from {entity.recentApplyStats.requestCount.toLocaleString()} requests
          </Typography>
        </Box>
        <Box>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography color="text.secondary" variant="caption">
              Apply rate
            </Typography>
            <Typography variant="caption">{applyRate}%</Typography>
          </Stack>
          <LinearProgress
            aria-label={`Apply rate for promo ${entity.id}`}
            color={failures > 0 ? 'warning' : 'success'}
            value={applyRate}
            variant="determinate"
            sx={{ mt: 0.5 }}
          />
        </Box>
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Typography variant="body2">{entity.usageCount.toLocaleString()} uses</Typography>
          <Typography variant="body2">{entity.assignmentCount.toLocaleString()} assignments</Typography>
          <Typography color={failures > 0 ? 'error.main' : 'text.secondary'} variant="body2">
            {failures.toLocaleString()} failures
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function renderPromoActivityCards({
  title,
  entities,
  isParentPromo,
}: {
  title: string;
  entities: Array<ActivityEntity>;
  isParentPromo?: boolean;
}) {
  if (entities.length === 0) {
    return null;
  }

  return (
    <Box component="section">
      <Typography variant="subtitle1">{title}</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
        {entities.map((entity) => (
          <React.Fragment key={entity.id}>{renderPromoActivityCard(entity, isParentPromo)}</React.Fragment>
        ))}
      </Stack>
    </Box>
  );
}

export default function PromoActivitySummary() {
  const { data, error, isFetching, isSuccess, refetch } = useGetPromoActivitySummary({ enabled: false });
  const summaryMetrics = useMemo<Array<ActivityMetric>>(() => {
    if (!data) {
      return [];
    }

    const { recentApplyStats } = data.totals;
    return [
      { label: 'Assignments', value: data.totals.assignmentCount },
      { label: 'Invokes', value: recentApplyStats.requestCount },
      { label: 'Applies', value: recentApplyStats.successfulCount },
      { label: 'Uses', value: data.totals.usageCount },
      {
        label: 'Failures',
        value: recentApplyStats.unsuccessfulRequestCount + recentApplyStats.unsuccessfulLogCount,
        tone: 'error',
      },
    ];
  }, [data]);
  const promoCards = useMemo(() => (data ? getMostActiveEntities(data.promoCodes, PROMO_CARD_LIMIT) : []), [data]);
  const parentPromoCards = useMemo(
    () => (data ? getMostActiveEntities(data.parentPromoCodes, PARENT_PROMO_CARD_LIMIT) : []),
    [data],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Inspect the cached aggregate activity window. It retains about 36 hours of telemetry and is not a durable
        reporting or reconciliation source. Amounts are quantities, not monetary spend.
      </Typography>

      <Box>
        <Button variant="contained" onClick={() => void refetch()} disabled={isFetching}>
          {isFetching ? 'Loading activity…' : data ? 'Refresh activity' : 'Load activity'}
        </Button>
      </Box>

      {isFetching && <CircularProgress size={24} aria-label="Loading recent promo activity" />}
      {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}

      {isSuccess && data && !isFetching && (
        <Stack spacing={3}>
          <Alert severity="info">
            Window: {formatDateWithClock(data.windowStart)} – {formatDateWithClock(data.windowEnd)}. Cached summary
            generated {formatDateWithClock(data.generatedAt)} and refreshes after {formatDateWithClock(data.expiresAt)}.
          </Alert>

          <Box component="section" aria-label="Activity summary">
            <Typography variant="subtitle1" gutterBottom>
              Activity summary
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {summaryMetrics.map((metric) => (
                <React.Fragment key={metric.label}>{renderActivityMetricCard(metric)}</React.Fragment>
              ))}
            </Stack>
          </Box>

          {renderPromoActivityCards({ title: 'Most active promos', entities: promoCards })}
          {renderPromoActivityCards({
            title: 'Parent promo activity',
            entities: parentPromoCards,
            isParentPromo: true,
          })}
        </Stack>
      )}
    </Stack>
  );
}
